/* eslint-disable no-unused-vars */
import React, { useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { skills } from "../../data/constants";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getInitials = (name) =>
  name.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 3).toUpperCase();

const colorPairs = [
  { bg: "#EEEDFE", color: "#3C3489", glow: "#854ce6" },
  { bg: "#E1F5EE", color: "#085041", glow: "#1D9E75" },
  { bg: "#E6F1FB", color: "#0C447C", glow: "#378ADD" },
  { bg: "#FAECE7", color: "#712B13", glow: "#D85A30" },
  { bg: "#FBEAF0", color: "#72243E", glow: "#D4537E" },
  { bg: "#FAEEDA", color: "#633806", glow: "#BA7517" },
  { bg: "#EAF3DE", color: "#27500A", glow: "#639922" },
];

const getColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colorPairs[Math.abs(hash) % colorPairs.length];
};

// ─── Skill Icon ───────────────────────────────────────────────────────────────

const SkillIcon = ({ src, name }) => {
  const [imgError, setImgError] = useState(false);
  const { bg, color } = getColor(name);

  if (!src || imgError)
    return <FallbackBadge bg={bg} color={color}>{getInitials(name)}</FallbackBadge>;

  return <SkillImage src={src} alt={name} onError={() => setImgError(true)} />;
};

// ─── 3D Tilt Card ─────────────────────────────────────────────────────────────

const TiltCard = ({ children, index }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { stiffness: 280, damping: 22 };
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [14, -14]), springConfig);
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-14, 14]), springConfig);
  const glowX = useTransform(x, [-0.5, 0.5], [0, 100]);
  const glowY = useTransform(y, [-0.5, 0.5], [0, 100]);

  const handleMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 100, scale: 0.8, rotateX: -15 }}
      whileInView={{
        opacity: 1,
        y: 0,
        scale: 1,
        rotateX: 0,
        transition: {
          delay: index * 0.13,
          duration: 0.75,
          ease: [0.16, 1, 0.3, 1],
        },
      }}
      viewport={{ once: true, amount: 0.15 }}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        perspective: 1000,
        width: "100%",
        height: "100%",            // ← fill grid row height
      }}
    >
      <CardInner>
        {children}
        <Spotlight
          style={{
            background: useTransform(
              [glowX, glowY],
              ([gx, gy]) =>
                `radial-gradient(circle at ${gx}% ${gy}%, rgba(133,76,230,0.18) 0%, transparent 60%)`
            ),
          }}
        />
        <EdgeGlow />
      </CardInner>
    </motion.div>
  );
};

// ─── Animated Skill Badge ─────────────────────────────────────────────────────

const SkillBadge = ({ item, index }) => {
  const [hovered, setHovered] = useState(false);
  const { glow } = getColor(item.name);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.4, y: 30 }}
      whileInView={{
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          delay: index * 0.045,
          type: "spring",
          stiffness: 450,
          damping: 16,
        },
      }}
      viewport={{ once: true, amount: 0.1 }}
      whileHover={{ scale: 1.08, y: -4, zIndex: 10 }}
      whileTap={{ scale: 0.92 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{ display: "flex", position: "relative" }}
    >
      <SkillItem hovered={hovered} glow={glow}>
        <motion.div
          animate={
            hovered
              ? { rotate: [0, -18, 18, -10, 10, 0], scale: [1, 1.3, 1] }
              : { rotate: 0, scale: 1 }
          }
          transition={{ duration: 0.45, ease: "easeInOut" }}
          style={{ display: "flex", alignItems: "center" }}
        >
          <SkillIcon src={item.image} name={item.name} />
        </motion.div>

        <span>{item.name}</span>

        {hovered && (
          <motion.div
            initial={{ x: "-120%", opacity: 0.7 }}
            animate={{ x: "220%", opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeIn" }}
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.25) 50%, transparent 65%)",
              borderRadius: "12px",
              pointerEvents: "none",
            }}
          />
        )}
      </SkillItem>
    </motion.div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Skills = () => (
  <Container id="skills">
    <Wrapper>
      <Title>Skills</Title>
      <Desc>
        Here are some of my skills on which I have been working on for the
        past 2 years.
      </Desc>

      <SkillsContainer>
        {skills.map((skill, cardIndex) => (
          <TiltCard key={skill.title} index={cardIndex}>
            <SkillCardContent>
              <SkillTitle>{skill.title}</SkillTitle>
              <SkillList>
                {skill.skills.map((item, itemIndex) => (
                  <SkillBadge key={item.name} item={item} index={itemIndex} />
                ))}
              </SkillList>
            </SkillCardContent>
          </TiltCard>
        ))}
      </SkillsContainer>
    </Wrapper>
  </Container>
);

export default Skills;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const borderPulse = keyframes`
  0%, 100% {
    border-color: rgba(133, 76, 230, 0.4);
    box-shadow: rgba(23, 92, 230, 0.08) 0px 4px 24px;
  }
  50% {
    border-color: rgba(133, 76, 230, 0.75);
    box-shadow: rgba(133, 76, 230, 0.22) 0px 8px 36px;
  }
`;

const edgeSlide = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  padding: 40px 0;
  overflow-x: hidden;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  padding: 0 16px;
  gap: 12px;
  box-sizing: border-box;
`;

export const Title = styled.div`
  font-size: 42px;
  text-align: center;
  font-weight: 600;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

export const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// ← KEY FIX: grid with equal columns + align-items stretch
const SkillsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 500px));
  align-items: stretch;
  margin-top: 30px;
  gap: 24px;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// ← height 100% so card stretches to match its grid sibling
const CardInner = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.card};
  border: 1px solid rgba(133, 76, 230, 0.4);
  border-radius: 16px;
  overflow: hidden;
  animation: ${borderPulse} 3.5s ease-in-out infinite;
`;

const Spotlight = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 16px;
  pointer-events: none;
  z-index: 0;
`;

const EdgeGlow = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #854ce6, #4776e6, transparent);
  animation: ${edgeSlide} 2.8s linear infinite;
  opacity: 0.5;
`;

// ← height 100% + flex column so content fills card vertically
const SkillCardContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 20px 28px 24px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 16px 16px 20px;
  }
`;

// ← min-height reserves 2 lines for every title regardless of text length
const SkillTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_secondary};
  margin-bottom: 20px;
  text-align: center;
  width: 100%;
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ← flex:1 + align-content:flex-start keeps badges top-aligned and fills space
const SkillList = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  width: 100%;
  flex: 1;
  align-content: flex-start;
`;

const SkillItem = styled.div`
  position: relative;
  font-size: 15px;
  font-weight: 400;
  color: ${({ hovered, theme }) =>
    hovered ? "#fff" : theme.text_primary + "cc"};
  border: 1px solid
    ${({ hovered, glow, theme }) =>
      hovered ? glow : theme.text_primary + "33"};
  background: ${({ hovered, glow }) =>
    hovered ? `${glow}28` : "transparent"};
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
  overflow: hidden;
  transition: color 0.2s, border-color 0.2s, background 0.2s, box-shadow 0.2s;
  box-shadow: ${({ hovered, glow }) =>
    hovered ? `0 6px 24px ${glow}55, 0 0 0 1px ${glow}33` : "none"};

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 11px;
  }
`;

const SkillImage = styled.img`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
`;

const FallbackBadge = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${({ bg }) => bg};
  color: ${({ color }) => color};
`;